'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import { GROQImageLoader } from '@/sanity/lib/definitions';
import { resolvePageLink } from '@/sanity/globals/CTA';
import { cn } from '@/lib/utils';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselApi,
} from '@/shadcn/ui/carousel';

const ease = [0.25, 0.1, 0.25, 1] as const;

import bg from '@/public/global/bg-art.jpg';

import NavBarV2 from '@/components/global/partials/HeaderV2';
import Footer from '@/components/global/partials/Footer';

import { PageAffiliateOverviewSchema } from '@/sanity/pages/PageAffiliateOverviewTemplate';
import SummerSaleTopBanner from '@/components/global/SummerSaleTopBanner';
import SummerSalePopup from '@/components/global/SummerSalePopup';
import AffiliateBanners from '@/components/global/AffiliateBanners';
import LaunchEventWidget from '@/components/global/LaunchEventWidget';

import bestsBG from '@/public/affiliate-overview/bests-bg.png';
import calloutBG from '@/public/affiliate-overview/callout_bg.png';

import heroStatue1 from '@/public/affiliate-overview/statue.png';
import ethBTC from '@/public/affiliate-overview/eth-btc.svg';
import solana from '@/public/affiliate-overview/solana.svg';
import heroBG from '@/public/affiliate-overview/heroBg.png';
import lines from '@/public/affiliate-overview/lines.svg';
import lilIcon from '@/public/affiliate-overview/lil-icon.svg';
import iq from '@/public/affiliate-overview/iq-and-you.svg';
import growthCardBG from '@/public/affiliate-overview/growth-card-bg.png';
import logoBlack from '@/public/global/logo-black.svg';
import growthDots from '@/public/affiliate-overview/growth-dots.png';
import footerDots from '@/public/affiliate-overview/footer-dots.png';
import doubleStatues from '@/public/affiliate-overview/double-statues.png';
import lastBG from '@/public/affiliate-overview/last-bg.png';
import lastStatue from '@/public/affiliate-overview/last-statue.png';
import dotsBG from '@/public/affiliate-overview/dots-bg.png';
import stepCardBG from '@/public/affiliate-overview/step-card-bg.png';

import GradualBlur from '@/shadcn/GradualBlur';
import Header from '../global/partials/Header';
import HeaderV2 from '@/components/global/partials/HeaderV2';
import ScrollingBanner from '../pageComponents/global/ScrollingBanner';

export default function PageAffiliateOverviewJSX({
  data,
}: {
  data: PageAffiliateOverviewSchema;
}) {
  const [bestsApi, setBestsApi] = useState<CarouselApi>();
  const [bestsCurrent, setBestsCurrent] = useState(0);
  const [bestsCount, setBestsCount] = useState(0);

  const [testimonialsApi, setTestimonialsApi] = useState<CarouselApi>();
  const [testimonialsCurrent, setTestimonialsCurrent] = useState(0);
  const [testimonialsCount, setTestimonialsCount] = useState(0);

  useEffect(() => {
    if (!bestsApi) return;

    const onSelect = () => {
      setBestsCurrent(bestsApi.selectedScrollSnap());
      setBestsCount(bestsApi.scrollSnapList().length);
    };

    onSelect();
    bestsApi.on('select', onSelect);
    bestsApi.on('reInit', onSelect);
    return () => {
      bestsApi.off('select', onSelect);
      bestsApi.off('reInit', onSelect);
    };
  }, [bestsApi]);

  useEffect(() => {
    if (!testimonialsApi) return;

    const onSelect = () => {
      setTestimonialsCurrent(testimonialsApi.selectedScrollSnap());
      setTestimonialsCount(testimonialsApi.scrollSnapList().length);
    };

    onSelect();
    testimonialsApi.on('select', onSelect);
    testimonialsApi.on('reInit', onSelect);
    return () => {
      testimonialsApi.off('select', onSelect);
      testimonialsApi.off('reInit', onSelect);
    };
  }, [testimonialsApi]);

  return (
    <>
      {data?.summerSale?.isActive && !data?.settings?.hideSummerSale && (
        <SummerSaleTopBanner
          data={data.summerSale}
          ctaUrl={data.summerSale?.ctaUrl || '#'}
          language={data.settings?.language}
        />
      )}
      {data?.summerSale?.isActive && !data?.settings?.hideSummerSale && (
        <SummerSalePopup
          data={data.summerSale}
          ctaUrl={data.summerSale?.ctaUrl || '#'}
          language={data.settings?.language}
        />
      )}
      <AffiliateBanners
        settings={data?.settings}
        enableFlashSale
      />
      <LaunchEventWidget
        data={data?.launchEvent}
        language={data?.settings?.language}
      />
      {data?.settings?.header && (
        <HeaderV2
          data={data.settings.header}
          headerStyle="light"
          headerType={'main'} // showBorder
          language={data.settings?.language}
        />
      )}
      {/* ── Hero ──────────────────────────────────────────── */}
      <section className="relative w-full overflow-clip">
        <div className="relative w-full bg-gradient-to-b from-[#F5F8F6] to-[#E6F2EB] overflow-clip h-[100rem] lg:h-[80.5rem]">
          <motion.div
            initial={{ opacity: 0, filter: 'blur(8px)' }}
            animate={{ opacity: 1, filter: 'blur(0px)' }}
            transition={{ duration: 1.2, ease }}
            className="hidden absolute inset-0 bottom-0 w-full lg:block z-[1]"
          >
            <Image
              src={heroBG}
              alt=""
              className="!w-full !h-full object-cover pointer-events-none"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease, delay: 0.3 }}
            className="absolute bottom-0 !translate-y-[10rem] lg:!translate-y-0 right-[-20rem] z-[5] lg:w-[93.5rem] lg:right-[32rem] w-[80rem]"
          >
            <Image
              src={heroStatue1}
              alt=""
              className="w-full pointer-events-none"
            />
          </motion.div>

          <div className="flex  relative flex-col h-full lg:justify-center z-[9] pt-[8rem] w-[min(140rem,calc(100%-4rem))] lg:w-[min(140rem,calc(100%-8rem))] mx-auto">
            {data?.heroText && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease, delay: 0.35 }}
                className="richtext flex flex-col gap-[2rem] lg:richtext-start max-w-[77.625rem] prose-em:font-blacker max-sm:prose-h1:text-[5rem] max-sm:prose-h1:tracking-[-0.25rem]"
              >
                <PortableText
                  value={data.heroText}
                  components={richTextComponent}
                />
              </motion.div>
            )}

            {data?.heroCta && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease, delay: 0.5 }}
                className="mt-[3rem] lg:[&_.button]:!min-h-[5.75rem] lg:w-fit"
              >
                <CTAButton data={data.heroCta} />
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* ── Deals ─────────────────────────────────────────── */}
      {(data?.dealsTitle || data?.dealsItems) && (
        <section className="relative w-full py-[10rem]">
          <div className="container relative mx-auto px-[3rem] z-[5] lg:px-[4rem]">
            {data?.dealsTitle && (
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.8, ease }}
                className={cn(
                  'richtext flex flex-col gap-[2rem]  prose-em:font-blacker ',
                  data.settings?.language === 'de'
                    ? ' max-sm:prose-h1:text-[4.25rem]'
                    : ' max-sm:prose-h1:text-[4.5rem]',
                )}
              >
                <PortableText
                  value={data.dealsTitle}
                  components={richTextComponent}
                />
              </motion.div>
            )}
            {data?.dealsItems && data.dealsItems.length > 0 && (
              <React.Fragment>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[2rem] mt-[4rem] w-[min(146.75rem,100%)] mx-auto">
                  {data.dealsItems.slice(0, 3).map((item, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 24 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.35 }}
                      transition={{ duration: 0.8, ease, delay: idx * 0.08 }}
                      className="flex flex-col text-center bg-white border lg:items-center gap-[1.5rem] p-[3rem]"
                    >
                      {item.icon && (
                        <Image
                          src={item.icon}
                          alt={''}
                          className="object-contain pointer-events-none w-[5rem] h-[5rem]"
                        />
                      )}
                      {item.text && (
                        <div className="richtext lg:richtext-center richtext-start prose-h3:text-[3rem] prose-h3:text-[#103037] prose-h3:font-[500] prose-h3:leading-[120%] prose-h3:tracking-[-.72px] lg:prose-p:text-[2rem] prose-p:text-[2.25rem]">
                          <PortableText
                            value={item.text}
                            components={richTextComponent}
                          />
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>

                <div className="grid grid-cols-1  lg:grid-cols-4 gap-[2rem] mt-[2rem] w-[min(146.75rem,100%)] mx-auto">
                  {data.dealsItems.slice(3, 5).map((item, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 24 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.35 }}
                      transition={{ duration: 0.8, ease, delay: idx * 0.08 }}
                      className="flex flex-col text-center bg-white border lg:col-span-2 lg:items-center gap-[1.5rem] p-[4rem]"
                    >
                      {item.icon && (
                        <Image
                          src={item.icon}
                          alt={''}
                          className="object-contain pointer-events-none w-[5rem] h-[5rem]"
                        />
                      )}
                      {item.text && (
                        <div
                          className={`richtext lg:richtext-center richtext-start prose-h3:text-[3rem] prose-h3:text-[#103037] prose-h3:font-[500] prose-h3:leading-[120%] prose-h3:tracking-[-.72px] lg:prose-p:text-[2rem] prose-p:text-[2.25rem]`}
                        >
                          <PortableText
                            value={item.text}
                            components={richTextComponent}
                          />
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </React.Fragment>
            )}
            {data?.dealsCta && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.8, ease, delay: 0.2 }}
                className="flex justify-center mt-[4rem] lg:[&_.button]:!min-h-[5.75rem]"
              >
                <CTAButton data={data.dealsCta} />
              </motion.div>
            )}
          </div>
        </section>
      )}

      {/* ── Bests ─────────────────────────────────────────── */}
      {(data?.bestsTitle || data?.bestsItems) && (
        <section className="relative w-full lg:p-[4.75rem]">
          <div className="flex relative flex-col bg-[#04271D] py-[7rem] gap-[7rem] overflow-clip lg:py-[15rem]">
            <div className="w-[30%] bg-gradient-to-r from-[#04271D] to-transparent absolute z-[9] h-full left-0 lg:block hidden pointer-events-none" />
            <div className="w-[30%] bg-gradient-to-l from-[#04271D] to-transparent absolute z-[9] h-full right-0 lg:block hidden pointer-events-none" />

            <Image
              src={dotsBG}
              alt=""
              className="object-cover absolute inset-0 w-full h-full pointer-events-none mix-blend-exclusion"
            />

            <div className="hidden absolute w-full h-[70%] lg:block pointer-events-none">
              <GradualBlur
                position="right"
                strength={1}
                width="20%"
                divCount={8}
                exponential
                zIndex={99}
                curve="ease-out"
                style={{ overflow: 'hidden' }}
              />
              <GradualBlur
                position="left"
                strength={1}
                width="20%"
                divCount={8}
                exponential
                zIndex={99}
                curve="ease-out"
                style={{ overflow: 'hidden' }}
              />
            </div>
            <div className="lg:px-0 px-[3rem] mb-[4rem] flex flex-col items-center m-auto">
              {data?.bestsTitle && (
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.35 }}
                  transition={{ duration: 0.8, ease }}
                  className="richtext flex flex-col gap-[2rem]  prose-em:font-blacker prose-headings:text-white prose-p:text-white max-w-[70rem]"
                >
                  <PortableText
                    value={data.bestsTitle}
                    components={richTextComponent}
                  />
                </motion.div>
              )}
            </div>
            {data?.bestsItems && data.bestsItems.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, ease, delay: 0.15 }}
                className="w-full"
              >
                <Carousel
                  opts={{ loop: true, align: 'center' }}
                  setApi={setBestsApi}
                  className="w-full"
                >
                  <CarouselContent className="-ml-[3rem]">
                    {data.bestsItems.map((item, idx) => (
                      <CarouselItem key={idx} className="pl-[3rem] basis-auto">
                        <div className="flex relative flex-col justify-end text-center bg-white lg:items-start gap-[1rem] p-[3rem] rounded-[.25rem] h-[33rem] overflow-clip shrink-0 w-[51.875rem]">
                          <Image
                            src={bestsBG}
                            alt=""
                            className="absolute inset-0 pointer-events-none z-[0]"
                          />
                          <div className="flex relative flex-col justify-between h-full lg:h-full gap-[2.5rem] z-[1]">
                            {item.invites && (
                              <div className="rounded-full bg-[#103037]/5 py-[1rem] px-[1.5rem] w-fit">
                                <p className="m-0 font-[500] leading-[150%]">
                                  {item.invites}
                                </p>
                              </div>
                            )}
                            <div className="flex flex-col gap-[2rem] lg:gap-[2.5rem]">
                              <div className="flex items-center gap-[1.25rem]">
                                {item.photo && (
                                  <Image
                                    src={item.photo}
                                    alt={''}
                                    loader={GROQImageLoader}
                                    className="object-cover rounded-full pointer-events-none w-[4rem] h-[4rem]"
                                  />
                                )}

                                {item.name && (
                                  <p className="m-0 font-[500] text-[2.25rem] text-[#103037]">
                                    {item.name}
                                  </p>
                                )}
                              </div>
                              <div className="flex flex-col items-start gap-[7.5rem]">
                                {item.value && (
                                  <p className="m-0 font-[600] items-start flex flex-col text-[#103037] lg:text-[4rem] tracking-[-0.12rem] leading-[120%] text-[5rem]">
                                    {item.value}
                                    {item.sufix && (
                                      <span className="opacity-60 font-[500] text-[2rem] tracking-[-0.06rem]">
                                        {item.sufix}
                                      </span>
                                    )}
                                  </p>
                                )}
                                {/* {item.invites && (
                                  <div className="hidden rounded-full lg:block bg-[#103037]/5 py-[1rem] px-[1.5rem]">
                                    <p className="m-0 font-[500] leading-[150%]">
                                      {item.invites}
                                    </p>
                                  </div>
                                )} */}
                              </div>
                            </div>
                          </div>
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>

                {/* Arrows + Progress Bar */}
                <div className="flex justify-center items-center gap-[2rem] mt-[4rem] px-[2rem]">
                  {/* Previous Button */}
                  <button
                    type="button"
                    onClick={() => bestsApi?.scrollPrev()}
                    className="flex flex-shrink-0 justify-center items-center text-white rounded-full border transition-all w-[6rem] h-[6rem] border-white/20 hover:bg-white/10"
                    aria-label="Previous slide"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="flex-shrink-0 h-auto rotate-180 w-[3rem]"
                    >
                      <path
                        d="M13.5 4.5L12.4275 5.54475L18.1125 11.25H3V12.75H18.1125L12.4275 18.4298L13.5 19.5L21 12L13.5 4.5Z"
                        fill="currentColor"
                      />
                    </svg>
                  </button>

                  {/* Progress Bar */}
                  <div className="overflow-hidden w-full rounded-full h-[0.5rem] bg-white/20 max-w-[34rem]">
                    <div
                      className="h-full rounded-full transition-all duration-300 ease-out bg-[#A3DEB2]"
                      style={{
                        width: bestsCount
                          ? `${((bestsCurrent + 1) / bestsCount) * 100}%`
                          : '0%',
                      }}
                    />
                  </div>

                  {/* Next Button */}
                  <button
                    type="button"
                    onClick={() => bestsApi?.scrollNext()}
                    className="flex flex-shrink-0 justify-center items-center text-white rounded-full border transition-all w-[6rem] h-[6rem] border-white/20 hover:bg-white/10"
                    aria-label="Next slide"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="flex-shrink-0 h-auto w-[3rem]"
                    >
                      <path
                        d="M13.5 4.5L12.4275 5.54475L18.1125 11.25H3V12.75H18.1125L12.4275 18.4298L13.5 19.5L21 12L13.5 4.5Z"
                        fill="currentColor"
                      />
                    </svg>
                  </button>
                </div>
              </motion.div>
            )}

            {data?.bestsCta && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.8, ease, delay: 0.2 }}
                className="flex justify-center mt-[3rem] lg:[&_.button]:!min-h-[5.75rem] px-[3rem]"
              >
                <CTAButton data={data.bestsCta} />
              </motion.div>
            )}
          </div>
        </section>
      )}

      {/* ── Partnership ───────────────────────────────────── */}
      {(data?.partnershipTitle || data?.partnershipItems) && (
        <section className="relative w-full p-[3rem] lg:p-[4.75rem]">
          <div className="relative w-full overflow-clip pb-[4rem] pt-[4rem] lg:pb-[12rem] lg:pt-[12rem] lg:px-[4rem] lg:rounded-[5rem]">
            <div className="w-[min(149rem,100%)] mx-auto flex flex-col lg:gap-[6rem] relative z-[1] gap-[4rem]">
              <div>
                {data?.partnershipTitle && (
                  <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.35 }}
                    transition={{ duration: 0.8, ease }}
                    className="richtext flex flex-col gap-[2rem]  prose-em:font-blacker"
                  >
                    <PortableText
                      value={data.partnershipTitle}
                      components={richTextComponent}
                    />
                  </motion.div>
                )}
              </div>
              <div>
                {data?.partnershipItems && data.partnershipItems.length > 0 && (
                  <div className="flex flex-col gap-[2rem]">
                    {data.partnershipItems.map((item, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.8, ease, delay: idx * 0.08 }}
                        className="flex relative justify-between items-center overflow-clip gap-[2rem] bg-[#EAEAEA] lg:pr-[6rem]"
                      >
                        {item.bg && (
                          <div
                            className={`lg:flex hidden self-stretch w-[66.5rem] flex-shrink-0 relative`}
                          >
                            <Image
                              src={stepCardBG}
                              alt=""
                              className={`object-cover absolute !w-full !h-full z-[1] mix-blend-luminosity `}
                            />
                            <Image
                              src={item.bg}
                              alt=""
                              loader={GROQImageLoader}
                              className={`object-cover !w-full !h-full relative z-[5] pointer-events-none ${idx === 0 && 'ml-[8rem]'}`}
                              quality={100}
                            />
                          </div>
                        )}
                        <div className="relative z-10 lg:p-0 w-[72.5rem] p-[3rem] lg:py-[3rem] lg:pl-[2rem]">
                          {item.text && (
                            <div className="richtext richtext-start prose-h3:text-[3.5rem] prose-h3:font-[500] prose-h3:mb-[3rem] prose-h6:bg-[#228E71] prose-h6:px-[1rem] prose-h6:py-[0.75rem] prose-h6:font-geist-mono prose-h6:tracking-[-0.0675rem] prose-h6:text-[2.25rem] prose-h6:font-[500] prose-h6:mb-[3rem] prose-h6:text-white prose-p:text-[2.25rem] prose-p:font-[400] prose-p:leading-[145%] prose-p:m-0 prose-p:text-black prose-strong:font-[500] prose-ul:my-[1rem] prose-ul:m-0 prose-ul:pl-[3rem] prose-li:text-[2.25rem] prose-li:text-black prose-li:font-geist">
                              <PortableText
                                value={item.text}
                                components={richTextComponent}
                              />
                            </div>
                          )}
                          {item.cta && idx !== 1 && (
                            <div className="mt-[2rem] [&_*]:!text-white lg:[&_.button]:!min-h-[5.75rem] lg:w-fit">
                              <CTAButton data={item.cta} className="" />
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}

                {/* Callout */}
                {data?.partnershipCallout && (
                  <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.8, ease }}
                    className="flex flex-col justify-between items-center lg:flex-row bg-[#04271D] mt-[2rem] p-[3rem] gap-[3rem] lg:p-[4rem]"
                  >
                    <div className="flex flex-col gap-[3rem]">
                      {data.partnershipCallout.text && (
                        <div className="richtext lg:richtext-start richtext-center:[&_span]:text-[#A3DEB2] [&_span]:text-white w-[min(50.625rem,100%)] lg:[&_span]:font-display lg:[&_span]:font-[700] [&_span]:font-instrument-sans [&_span]:font-[500] prose-h3:text-white prose-h3:font-[500] prose-headings:text-[4.5rem] prose-headings:leading-[120%] prose-headings:trackgin-[-0.135rem]">
                          <PortableText
                            value={data.partnershipCallout.text}
                            components={richTextComponent}
                          />
                        </div>
                      )}
                      {data.partnershipCallout.cta && (
                        <div className="lg:[&_.button]:!min-h-[5.75rem] lg:block hidden lg:w-fit">
                          <CTAButton data={data.partnershipCallout.cta} />
                        </div>
                      )}
                    </div>
                    {data.partnershipCallout.steps &&
                      data.partnershipCallout.steps.length > 0 && (
                        <div className="flex relative flex-col justify-center w-[min(62.75rem,100%)] gap-[2rem]">
                          <div className="absolute top-0 translate-y-[145%] w-[.25rem] h-[5.5rem] bg-[#A3DEB2] lg:left-[9%] left-[11.5%]" />
                          <div className="absolute top-0 translate-y-[365%] w-[.25rem] h-[5.5rem] bg-[#A3DEB2] lg:left-[9%] left-[11.5%]" />
                          {data.partnershipCallout.steps.map((step, idx) => (
                            <div
                              key={idx}
                              className="flex items-center gap-[2.25rem] bg-white/[8%] border border-white/10 rounded-[0.25rem] p-[1.5rem]"
                            >
                              <div className="rounded-full border-[2px] border-[#A3DEB2] p-[.5rem] shrink-0">
                                <div className="rounded-full border w-[5rem] h-[5rem] border-white/[15%] flex items-center justify-center">
                                  <p className="m-0 text-[#A3DEB2] text-[2.25rem]">
                                    {idx + 1}
                                  </p>
                                </div>
                              </div>
                              <p className="m-0 text-white text-[2.25rem] font-geist-mono tracking-[-0.11875rem]">
                                {step.text}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}

                    {data.partnershipCallout.cta && (
                      <div className="lg:[&_.button]:!min-h-[5.75rem] lg:hidden w-full">
                        <CTAButton data={data.partnershipCallout.cta} />
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Growth ────────────────────────────────────────── */}
      {(data?.growthTitle || data?.growthItems) && (
        <section className="relative w-full py-[2rem] lg:pt-[10rem] lg:pb-[20rem]">
          <Image
            src={growthDots}
            alt=""
            className="absolute top-0 pointer-events-none mix-blend-exclusion"
          />
          <div className="container flex items-center flex-col mx-auto lg:px-0 lg:pb-0 px-[3rem] gap-[4rem] pb-[4rem]">
            {data?.growthTitle && (
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.8, ease }}
                className="richtext flex flex-col gap-[2rem]  prose-em:font-blacker prose-headings:text-[#103037] prose-p:text-[#103037] max-w-[70rem]"
              >
                <PortableText
                  value={data.growthTitle}
                  components={richTextComponent}
                />
              </motion.div>
            )}
            {data?.growthItems && data.growthItems.length > 0 && (
              <div className="grid lg:grid-cols-3 w-[min(170rem,100%)] gap-[1.5rem] items-end grid-cols-1 mx-auto">
                {data.growthItems.map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.8, ease, delay: idx * 0.1 }}
                    className={`overflow-hidden bg-[#F2F2F2] lg:w-[55rem] relative ${idx === 0 ? 'lg:min-h-[73rem] min-h-[60rem]' : idx === 1 ? 'lg:min-h-[80rem] min-h-[73rem] lg:mb-[8rem]' : 'lg:min-h-[85.25rem] min-h-[83.5rem] lg:mb-[16rem]'} p-[3rem]`}
                  >
                    {item.bg1 && (
                      <Image
                        src={item.bg1}
                        alt=""
                        quality={100}
                        className="absolute right-0 bottom-0 left-0 w-full pointer-events-none"
                      />
                    )}
                    <Image
                      src={growthCardBG}
                      alt=""
                      className="absolute inset-0 w-full h-full pointer-events-none z-[1] overflow-clip mix-blend-exclusion"
                    />
                    <div className="flex relative z-10 flex-col gap-[2rem]">
                      <div className="flex flex-col richtext richtext-start gap-[1rem] prose-h3:text-[#103037] prose-h3:text-[3.5rem] prose-h3:tracking-[-0.15rem] prose-h3:font-semibold">
                        <PortableText
                          value={item.title}
                          components={richTextComponent}
                        />
                      </div>

                      <div className="flex flex-col gap-[1rem] mb-[42rem] lg:mb-[50rem]">
                        {item.checks.map((check, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-[1.5rem]"
                          >
                            <svg
                              width="24"
                              height="24"
                              className="!shrink-0 w-[3rem] h-[3rem]"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <rect
                                width="24"
                                height="24"
                                fill="white"
                                fillOpacity="0.01"
                                className="mix-blend-multiply"
                              />
                              <path
                                d="M9.75 18L3 11.25L4.0605 10.1895L9.75 15.8782L19.9395 5.68945L21 6.74995L9.75 18Z"
                                fill="#228E71"
                              />
                            </svg>
                            <p className="m-0 text-[#103037]/90 text-[2.25rem]">
                              {check.text}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Trust ─────────────────────────────────────────── */}
      {(data?.trustTitle || data?.trustItems || data?.trustTestimonials) && (
        <section className="relative w-full">
          <div className="flex flex-col items-center px-[3rem] bg-[#001B14] py-[7rem] gap-[2rem] pb-[15rem] lg:pt-[15rem] lg:pb-[28rem] lg:px-[4rem]">
            {data?.trustTitle && (
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.8, ease }}
                className="richtext flex flex-col gap-[2rem]  prose-em:font-blacker prose-headings:text-white prose-p:text-white max-w-[70rem] mx-auto text-center"
              >
                <PortableText
                  value={data.trustTitle}
                  components={richTextComponent}
                />
              </motion.div>
            )}

            <div className="flex flex-col mx-auto w-full gap-[3rem]">
              {/* Trust stats */}
              {data?.trustItems && data.trustItems.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-[3rem] lg:mt-[4rem] w-[min(170.5rem,100%)] mx-auto mt-[2rem] gap-[2rem] ">
                  {data.trustItems.map((item, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 24 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.2 }}
                      transition={{ duration: 0.8, ease, delay: idx * 0.08 }}
                      className="flex flex-col-reverse justify-between border lg:flex-row lg:items-center gap-[0.5rem] border-white/20  px-[3rem] py-[3rem] lg:px-[5rem] lg:py-[4.5rem] bg-white/[3%]"
                    >
                      {item.text && (
                        <div className="richtext richtext-start lg:max-w-[45%] prose-p:text-white/80 prose-p:text-[2.25rem] prose-p:font-[500]">
                          <PortableText
                            value={item.text}
                            components={richTextComponent}
                          />
                        </div>
                      )}

                      {item.value && (
                        <p className="m-0 text-white font-[400] tracking-[-2.8px] text-[4rem] lg:text-[7rem]">
                          {item.value}
                        </p>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Testimonials */}
              {data?.trustTestimonials && data.trustTestimonials.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.8, ease, delay: 0.15 }}
                  className="overflow-hidden mx-auto w-full max-w-[172.5rem]"
                >
                  <Carousel
                    opts={{ loop: true, align: 'start' }}
                    setApi={setTestimonialsApi}
                    className="w-full"
                  >
                    <CarouselContent className="ml-0">
                      {data.trustTestimonials.map((testimonial, idx) => (
                        <CarouselItem
                          key={idx}
                          className="pl-0 basis-full px-[1rem]"
                        >
                          <div className="flex flex-col justify-center items-center lg:flex-row p-[1.5rem] bg-white/10 gap-[1.5rem] cursor-grab lg:gap-[5rem] lg:h-[74.875rem] lg:p-[2rem] active:cursor-grabbing">
                            {testimonial.image && (
                              <Image
                                src={testimonial.image}
                                alt={''}
                                quality={100}
                                className="object-cover w-full pointer-events-none lg:w-auto lg:h-full shrink-0 lg:max-h-none"
                              />
                            )}
                            <div className="flex flex-col gap-[1.5rem] lg:p-[1rem]">
                              <div className="flex flex-col bg-white/[3%] gap-[2rem] p-[2rem]  lg:gap-[3rem] lg:p-[5rem]">
                                <svg
                                  className="!shrink-0 w-[3rem] h-[3rem] lg:w-[5.75rem] lg:h-[5.625rem]"
                                  width="46"
                                  height="45"
                                  viewBox="0 0 46 45"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    opacity="0.9"
                                    d="M27.7986 45L35.741 18.9033H27.3022V0H46V16.5613L36.0719 45H27.7986ZM0.496402 45L8.43885 18.9033H0V0H18.6978V16.5613L8.76978 45H0.496402Z"
                                    fill="#fff"
                                  />
                                </svg>
                                {testimonial.quote && (
                                  <div className="richtext richtext-start [&_*]:select-none prose-p:text-[1.75rem] prose-p:font-[400] prose-p:leading-[150%] prose-p:text-white/90 lg:prose-p:text-[2.25rem]">
                                    <PortableText
                                      value={testimonial.quote}
                                      components={richTextComponent}
                                    />
                                  </div>
                                )}
                              </div>
                              <div className="w-full lg:px-0 px-[1rem]">
                                {testimonial.name && (
                                  <p className="m-0 text-white font-[500] text-[2.25rem] lg:text-[3rem]">
                                    {testimonial.name}
                                  </p>
                                )}
                                {testimonial.position && (
                                  <p className="m-0 font-[500] text-white/70 text-[1.75rem] lg:text-[2.25rem]">
                                    {testimonial.position}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                  </Carousel>

                  {/* Arrows + Stepper */}
                  <div className="flex justify-center items-center gap-[1.5rem] px-[1rem] mt-[4rem] lg:gap-[2rem] lg:px-[2rem]">
                    {/* Previous Button */}
                    <button
                      type="button"
                      onClick={() => testimonialsApi?.scrollPrev()}
                      className="flex flex-shrink-0 justify-center items-center text-white/50 rounded-full bg-white/[15%] transition-all w-[5rem] h-[5rem]  lg:w-[6rem] lg:h-[6rem] hover:bg-white/10"
                      aria-label="Previous testimonial"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        className="flex-shrink-0 h-auto rotate-180 w-[2.5rem] lg:w-[3rem]"
                      >
                        <path
                          d="M13.5 4.5L12.4275 5.54475L18.1125 11.25H3V12.75H18.1125L12.4275 18.4298L13.5 19.5L21 12L13.5 4.5Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>

                    {/* Stepper Dots */}
                    <div className="flex items-center gap-[0.75rem] lg:gap-[1rem]">
                      {data.trustTestimonials.map((_, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => testimonialsApi?.scrollTo(idx)}
                          className={cn(
                            'rounded-full transition-all duration-300',
                            idx === testimonialsCurrent
                              ? 'w-[7.75rem] h-[.5rem] bg-[#A3DEB2] '
                              : 'w-[7.75rem] h-[.5rem] bg-[#A3DEB2]/30 hover:bg-[#A3DEB2]/50 ',
                          )}
                          aria-label={`Go to testimonial ${idx + 1}`}
                        />
                      ))}
                    </div>

                    {/* Next Button */}
                    <button
                      type="button"
                      onClick={() => testimonialsApi?.scrollNext()}
                      className="flex flex-shrink-0 justify-center items-center rounded-full bg-white/[15%] transition-all text-white/50 w-[5rem] h-[5rem]  lg:w-[6rem] lg:h-[6rem] hover:bg-white/10"
                      aria-label="Next testimonial"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        className="flex-shrink-0 h-auto w-[2.5rem] lg:w-[3rem]"
                      >
                        <path
                          d="M13.5 4.5L12.4275 5.54475L18.1125 11.25H3V12.75H18.1125L12.4275 18.4298L13.5 19.5L21 12L13.5 4.5Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                  </div>
                </motion.div>
              )}
            </div>

            <div className="absolute inset-x-0 bottom-0 z-50 overflow-clip h-[10rem]">
              <ScrollingBanner
                theme="dark"
                className="bg-transparent border-y-0"
                words={data.scrollingWords?.words}
                wordsI18n={data.scrollingWords?.wordsI18n}
              />
            </div>
          </div>
        </section>
      )}

      {/* ── Proof ─────────────────────────────────────────── */}
      {(data?.proofTitle || data?.proofItems) && (
        <section className="relative w-full py-[6rem] lg:py-[10rem]">
          <div className="lg:w-[min(140rem,calc(100%-8rem))] w-[min(140rem,calc(100%-4rem))] mx-auto px-[2rem] lg:px-[4rem]">
            {data?.proofTitle && (
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.8, ease }}
                className="richtext richtext-start lg:richtext-center prose-h6:font-blacker prose-h6:italic prose-h6:text-[#103037] prose-h6:text-[2.25rem] prose-h6:mb-[2rem] prose-headings:font-geist prose-headings:font-[500]"
              >
                <PortableText
                  value={data.proofTitle}
                  components={richTextComponent}
                />
              </motion.div>
            )}
            {data?.proofItems && data.proofItems.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-[2rem] mt-[6.3rem]">
                {data.proofItems.map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.8, ease, delay: idx * 0.08 }}
                    className="flex flex-col bg-[#B4B4B4]/[15%] p-[3rem] gap-[4rem] lg:p-[4rem]"
                  >
                    <Image
                      src={lilIcon}
                      alt=""
                      className="hidden p-0 m-0 pointer-events-none lg:block w-[4rem] h-[4rem]"
                    />
                    {item.text && (
                      <div className="richtext richtext-start prose-h3:text-[#103037] prose-h3:font-[600] prose-h3:text-[3rem] lg:prose-h3:text-[3.5rem] lg:prose-p:w-[80%] prose-p:w-[70%] prose-p:mt-[2rem]">
                        <PortableText
                          value={item.text}
                          components={richTextComponent}
                        />
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Last ──────────────────────────────────────────── */}
      {(data?.lastTitle || data?.lastCta) && (
        <section className="relative w-full p-[1rem] bg-[#B4B4B4]/[15%] lg:p-[5rem] lg:h-[120rem] overflow-clip">
          <div className="relative w-full text-center pt-[6.5rem] h-[82.5rem] overflow-clip pb-[90rem] lg:h-[119.375rem] lg:pt-[12.25rem]">
            <div className="px-[2.625rem]">
              {data?.lastTitle && (
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.35 }}
                  transition={{ duration: 0.8, ease, delay: 0.15 }}
                  className="richtext mx-auto lg:[&_span]:font-display lg:[&_span]:font-[700] [&_span]:font-instrument-sans [&_span]:font-[600] [&_span]:text-[#103037] lg:[&_span]:text-[#65A38A] z-[5] relative prose-h6:font-blacker prose-h6:italic prose-h6:text-[#103037] prose-h6:text-[2.25rem] prose-h6:mb-[2rem] prose-p:w-[min(63.625rem,100%)] prose-p:text-[2rem] prose-strong:!font-[600] prose-em:font-[400] prose-em:text-[#003828] prose-em:font-blacker prose-headings:font-geist prose-headings:font-[500] prose-headings:mb-[2rem]"
                >
                  <PortableText
                    value={data.lastTitle}
                    components={richTextComponent}
                  />
                </motion.div>
              )}
              {data?.lastCta && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.35 }}
                  transition={{ duration: 0.8, ease, delay: 0.3 }}
                  className="flex relative justify-center mt-[3rem] z-[5] lg:[&_.button]:!min-h-[5.75rem]"
                >
                  <CTAButton data={data.lastCta} />
                </motion.div>
              )}
            </div>
          </div>
          <Image
            src={lastBG}
            alt=""
            quality={100}
            className="absolute inset-0 pointer-events-none z-[1] mix-blend-exclusion"
          />

          <Image
            src={lastStatue}
            alt=""
            quality={100}
            className="absolute bottom-0 left-1/2 -translate-x-1/2 pointer-events-none z-[1]"
          />
          <div className="absolute bottom-0 w-full bg-gradient-to-t h-[22.25rem] from-[#F4F4F4] to-[#F4F4F4]/0 z-[2]" />
        </section>
      )}
      {/* ── Footer Disclaimer ─────────────────────────────── */}
      {(data?.footerDisclaimerCopyright ||
        (data?.footerDisclaimerLegalLinks &&
          data.footerDisclaimerLegalLinks.length > 0) ||
        data?.footerDisclaimerText) && (
        <section className="relative w-full bg-[#F4F4F4] py-[6rem] px-[3rem] lg:py-[8rem] lg:px-[4rem]">
          <Image
            src={footerDots}
            alt=""
            className="object-cover absolute inset-0 w-full h-full pointer-events-none mix-blend-exclusion"
          />
          <div className="container flex flex-col mx-auto gap-[4rem] w-[min(140rem)]">
            {(data?.footerDisclaimerCopyright ||
              (data?.footerDisclaimerLegalLinks &&
                data.footerDisclaimerLegalLinks.length > 0)) && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.6, ease }}
                className="flex relative flex-col justify-between items-center lg:flex-row gap-[2rem] lg:h-[3.5rem]"
              >
                {data?.footerDisclaimerCopyright && (
                  <p className="m-0 text-[#103037]/70 text-[1.75rem] lg:text-[2rem]">
                    {data.footerDisclaimerCopyright}
                  </p>
                )}
                <div className="lg:absolute lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2">
                  <Image
                    src={logoBlack}
                    alt=""
                    className="pointer-events-none"
                  />
                </div>
                {data?.footerDisclaimerLegalLinks &&
                  data.footerDisclaimerLegalLinks.length > 0 && (
                    <ul className="flex flex-wrap items-center p-0 m-0 list-none gap-x-[1rem] gap-y-[1rem]">
                      {data.footerDisclaimerLegalLinks.map((item, idx) => {
                        const href =
                          resolvePageLink(
                            item.link,
                            data?.settings?.language,
                          ) ?? '#';
                        const openInNewTab = item.link?.openInNewTab;
                        const isLast =
                          idx ===
                          (data.footerDisclaimerLegalLinks?.length ?? 0) - 1;
                        return (
                          <React.Fragment key={idx}>
                            <li className="m-0">
                              <a
                                href={href}
                                target={openInNewTab ? '_blank' : undefined}
                                rel={
                                  openInNewTab
                                    ? 'noopener noreferrer'
                                    : undefined
                                }
                                className="transition-all hover:underline text-black/80 text-[1.75rem] underline-offset-4 font-geist lg:text-[2rem]"
                              >
                                {item.title}
                              </a>
                            </li>
                            {!isLast && (
                              <p className="m-0 text-black/80 text-[1.75rem] lg:text-[2rem]">
                                /
                              </p>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </ul>
                  )}
              </motion.div>
            )}
            <div className="w-full border-t h-[.125rem] border-t-black/10" />
            {data?.footerDisclaimerText && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, ease, delay: 0.1 }}
                className="richtext richtext-center prose-p:text-black/60 prose-p:text-[1.75rem] prose-p:leading-[150%] prose-p:font-geist"
              >
                <PortableText
                  value={data.footerDisclaimerText}
                  components={richTextComponent}
                />
              </motion.div>
            )}
          </div>
        </section>
      )}

      {/* {data?.settings?.footer && ( */}
      {/*   <Footer */}
      {/*     data={data.settings.footer} */}
      {/*     slug={data?.settings?.slug?.current} */}
      {/*   /> */}
      {/* )} */}
    </>
  );
}
